import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getBranchListAction } from '../actions/job-action/branch.action';
import { getJobDetailsAction, jobDetailsSaveCrewNotesAction } from '../actions/job-action/get-job-details.action';
import { getJobListWithCrewAction } from '../actions/job-action/get-jobs-with-crew.action';
import { searchJobListAction } from '../actions/job-action/search.action';
import { ALERT_DATA } from '../constants/alert-list';
import {
  Branch,
  DateString,
  ErrorState,
  GenericObject,
  Job,
  JobsState,
  MoveType,
  OneJob,
  Warning,
} from '../types/store.types';

const initialState: JobsState = {
  loading: false,
  searchLoading: false,
  jobsList: null,
  jobsListJunk: null,
  junkStatistics: null,
  uniqueCrewBuilderGroups: [],
  unScheduledJobsList: [],
  jobsListLoading: false,
  branchJobStats: null,
  jobSearchList: [],
  alertsList: ALERT_DATA,
  branchList: [],
  currentBranch: null,
  error: null,
  reSizeWindow: true,
  accessibility: false,
  jobsWarning: [],
  crewCardWarning: [],
  calenderDate: null,
  jobDetailsData: {},
  customerInfo: {
    drawer: false,
    loader: false,
  },
  crewBuilderModal: false,
  crewBuilderDrawer: undefined,
  jobDetailsDataLoading: false,
  filteredList: [],
  confirmJobList: [],
  jobDetails: {},
  modalState: {},
  selectedFilter: 'All',
  customerInfoDetails: {},
  customerInfoDetailsCopy: {},
  laborTimesCustomerInfoDetails: {},
  detailsPayload: {},
  detailsPayloadCopu: {},
  saveModalOpen: false,
  modalKey: '',
  customerinfoMaterialsData: null,
  customerinfoLaborData: null,
  jobHistory: [],
  junkCheckboxValues: {
    sms: false,
    email: false,
  },
  fvpCheckboxValues: {
    sms: false,
    email: false,
  },
  junkSendInfo: null,
  paymentSummary: {},
  paymentFormSummary: {},
  paymentHistory: [],
  paymentDates: [],
  invoiceDetails: {},
  invoiceDetailsJunk: {},
  isProcessPayment: false,
  isProcessRefund: false,
  customerInfoForPayment: {},
  invoiceDetailsSummary: {},
  moveTypeList: [],
  globalJobId: '',
  selectJobId: undefined,
  dragJobServiceType: '',
  dragJobId: '',
  dragJobSlot: '',
  tabIndex: 0,
  dragJobBookedDate: '',
  scheduleInfo: {
    drawer: false,
    loader: false,
  },
  addJobs: (_job: Job) => {
    // no op for now
  },
};

export interface JobBranch {
  label: string;
  branchId: string;
  dispatchTime?: string;
}

export interface JobBranchInput {
  _id: string;
  name: string;
  dispatch_start_time?: string;
}

const transformBranches = (branches: JobBranchInput[]): JobBranch[] => {
  return branches.map((branch) => ({
    label: branch?.name,
    branchId: branch?._id,
    dispatchTime: branch?.dispatch_start_time,
  }));
};

export const jobSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    addJobs: (state, action: PayloadAction<OneJob[]>) => {
      state.jobsList = action.payload;
    },
    moveTypes: (state, action: PayloadAction<MoveType[]>) => {
      state.moveTypeList = action.payload;
    },
    updateCurrentBranch: (state, action: PayloadAction<Branch>) => {
      state.currentBranch = action.payload;
    },
    addJobsWarning: (state, action: PayloadAction<Warning[]>) => {
      if (action.payload === null) {
        state.jobsWarning = [];
      } else {
        state.jobsWarning = action.payload;
      }
    },
    addCrewCardWarning: (state, action: PayloadAction<Warning[]>) => {
      if (action.payload === null) {
        state.crewCardWarning = [];
      } else {
        state.crewCardWarning = action.payload;
      }
    },
    toggleSwitch: (state, action: PayloadAction<boolean>) => {
      state.reSizeWindow = action.payload;
    },
    toggleCrewBuilderModal: (state, action: PayloadAction<boolean>) => {
      state.crewBuilderModal = action.payload;
    },
    toggleCrewBuilderDrawer: (state, action: PayloadAction<any>) => {
      state.crewBuilderDrawer = action.payload;
    },
    toggleAccessibility: (state, action: PayloadAction<boolean>) => {
      state.accessibility = action.payload;
    },
    updateCalenderDate: (state, action: PayloadAction<DateString>) => {
      state.calenderDate = action.payload;
    },
    clearSearchResult: (state) => {
      state.jobSearchList = [];
    },
    toggleCustomerInfoDrawer: (state, action: PayloadAction<boolean>) => {
      state.customerInfo.drawer = action.payload;
    },
    handleBranchUpdate: (state, action: PayloadAction<JobBranchInput[]>) => {
      const branchListData = transformBranches(action?.payload);
      state.branchList = branchListData.sort((a, b) => a.label.localeCompare(b.label));
    },
    handleUpdateJobsWithCrew: (state, action: PayloadAction<OneJob[]>) => {
      const jobData = [
        {
          crew: {
            drivers: [],
            helpers: [],
            trucks: [],
            type: 'unblocked',
          },
          jobs: [],
        },
        {
          crew: {
            drivers: [],
            helpers: [],
            trucks: [],
            type: 'addSlot',
          },
          jobs: [],
        },
      ];
      state.jobsList = action.payload && action.payload.length > 0 ? [...action.payload, ...jobData] : [];
    },

    handleUpdateJobsWithCrewJunk: (state, action: PayloadAction<Job[]>) => {
      state.jobsListJunk = action.payload && action.payload.length > 0 ? [...action.payload] : [];
    },

    getJunkStatistics: (state, action: PayloadAction<GenericObject>) => {
      state.junkStatistics = action.payload || {};
    },

    createNewUnblockJobs: (state, action: PayloadAction<OneJob[]>) => {
      const jobData = [
        {
          crew: {
            drivers: [],
            helpers: [],
            trucks: [],
            type: 'unblocked',
          },
          jobs: [],
        },
        {
          crew: {
            drivers: [],
            helpers: [],
            trucks: [],
            type: 'addSlot',
          },
          jobs: [],
        },
      ];
      if (action?.payload?.length > 0) {
        const newJobs = [...action.payload];
        newJobs.pop();
        state.jobsList = [...newJobs, ...jobData];
      }
    },
    handleUpdateDraggedJobsWithCrew: (state, action) => {
      if (action?.payload?.draggedJob?._id) {
        const jobsListNew: OneJob[] = JSON.parse(JSON.stringify(state.jobsList));
        const draggedJobNew = JSON.parse(JSON.stringify(action?.payload?.draggedJob));

        jobsListNew[action?.payload?.dragIndex].jobs = jobsListNew[action?.payload?.dragIndex]?.jobs?.filter(
          (job) => job._id !== action?.payload?.draggedJob?._id,
        );

        // Mapping object for time slots
        const timeSlotMap = {
          starter: { start: '6:30', end: '8:00' },
          hanger: { start: '10:30', end: '12:00' },
          flyer: { start: '13:30', end: '15:00' },
          finisher: { start: '17:00', end: '18:30' },
        };

        // Find the slot type based on the `includes` check
        const dropSlotType: keyof typeof timeSlotMap | undefined = Object.keys(timeSlotMap).find((key) =>
          action.payload.dropSlot.toLowerCase().includes(key),
        ) as keyof typeof timeSlotMap | undefined;

        // If a matching slot type is found, adjust times
        const startTime = action?.payload?.startTime;
        const endTime = action?.payload?.endTime;
        if (dropSlotType) {
          const { start, end } = dropSlotType ? timeSlotMap[dropSlotType] : { start: '', end: '' };
          draggedJobNew.job_starttime = startTime || adjustTime(action?.payload?.draggedJob.job_starttime, start);
          draggedJobNew.job_endtime = endTime || adjustTime(action?.payload?.draggedJob.job_endtime, end);
          draggedJobNew.newLoader = true;
          draggedJobNew.slot = action?.payload?.dropSlot;
        }

        jobsListNew[action?.payload?.dropIndex]?.jobs?.push(draggedJobNew);

        state.jobsList = jobsListNew;
      }
    },
    handleDragJobServiceType: (state, action) => {
      state.dragJobServiceType = action.payload;
    },
    handleDragJobId: (state, action) => {
      state.dragJobId = action.payload;
    },
    handleJobBookedDate: (state, action) => {
      state.dragJobBookedDate = action.payload;
    },
    handleDragJobSlot: (state, action) => {
      state.dragJobSlot = action.payload;
    },
    handleUpdateUniqueCrewBuilderGroups: (state, action) => {
      state.uniqueCrewBuilderGroups = action.payload;
    },
    handleUpdateUnscheduledJobs: (state, action) => {
      state.unScheduledJobsList = action.payload;
    },
    handleUpdateCustomerDetail: (state, action) => {
      state.jobDetailsData = action.payload;
    },
    handleSaveJobID: (state, action) => {
      state.globalJobId = action.payload;
    },
    filterJobList: (state, action) => {
      state.selectedFilter = action.payload;
      const filterType = action.payload.toLowerCase();

      // Update the filtered list based on the selected filter type
      state.filteredList = getFilteredJobs(state?.confirmJobList, filterType);
    },
    clearFilter: (state, _action) => {
      state.selectedFilter = '';
    },
    handleFetchAllConfirmJobList: (state, { payload }) => {
      state.confirmJobList = payload;
      state.filteredList = getFilteredJobs(state?.confirmJobList, state?.selectedFilter);
    },
    openModalAfterSave: (state, { payload }) => {
      state.modalState = payload;
    },
    handleUpdateCustomerInfo: (state, action) => {
      state.loading = action.payload;
    },
    fetchCustomerInfoDetails: (state, { payload }) => {
      const data = {
        ...payload,
        customer: {
          ...payload?.customer,
          fullName: `${payload?.customer?.first_name === 'null' ? '' : payload?.customer?.first_name || ''} ${
            payload?.customer?.last_name === 'null' ? '' : payload?.customer?.last_name || ''
          }`.trim(),
        },
      };
      state.customerInfoDetails = data;
      state.customerInfoDetailsCopy = data;
      state.laborTimesCustomerInfoDetails = data;
      state.jobHistory = data.history;
    },
    fetchLabortimesCustomerInfoDetails: (state, { payload }) => {
      const data = {
        ...payload,
        customer: {
          ...payload?.customer,
          fullName: `${payload?.customer?.first_name === 'null' ? '' : payload?.customer?.first_name || ''} ${
            payload?.customer?.last_name === 'null' ? '' : payload?.customer?.last_name || ''
          }`.trim(),
        },
      };
      state.laborTimesCustomerInfoDetails = data;
    },
    handleCustomerInfoDetailsChange: (state, { payload }) => {
      state.customerInfoDetails = { ...payload };
    },
    handleSaveModalOpen: (state, { payload }) => {
      state.saveModalOpen = payload.open;
      state.modalKey = payload.type;
    },
    fetchMaterialsItemsData: (state, { payload }) => {
      state.customerinfoMaterialsData = payload;
    },
    fetchLaborTimesData: (state, { payload }) => {
      state.customerinfoLaborData = payload;
    },
    handleJunkCheckboxValues: (state, { payload }) => {
      state.junkCheckboxValues = payload;
    },
    handleFvpCheckboxValues: (state, { payload }) => {
      state.fvpCheckboxValues = payload;
    },
    handleClearCheckBox: (state) => {
      state.junkCheckboxValues = {
        sms: false,
        email: false,
      };
      state.fvpCheckboxValues = {
        sms: false,
        email: false,
      };
    },
    fetchJunkInfoData: (state, { payload }) => {
      state.junkSendInfo = payload;
    },
    updateLaborTimesData: (state, { payload }) => {
      state.customerinfoLaborData = payload;
    },
    fetchPaymentSummaryData: (state, { payload }) => {
      state.paymentSummary = payload;
    },
    fetchPaymentFormData: (state, { payload }) => {
      state.paymentFormSummary = payload;
    },
    fetchPaymentHistoryData: (state, { payload }) => {
      state.paymentHistory = payload;
    },
    fetchPaymentDates: (state, { payload }) => {
      state.paymentDates = payload;
    },
    fetchSelectIndex: (state, { payload }) => {
      state.tabIndex = payload;
    },
    fetchInvoiceDetailsData: (state, { payload }) => {
      state.invoiceDetails = payload;
    },
    fetchInvoiceDetailsDataJunk: (state, { payload }) => {
      state.invoiceDetailsJunk = payload;
    },
    initiatePayment: (state, action) => {
      state.isProcessPayment = action.payload;
    },
    initiateRefund: (state, action) => {
      state.isProcessRefund = action.payload;
    },
    handleBranchJobStats: (state, action) => {
      state.branchJobStats = action.payload;
    },
    addCustomerInfoForPayment: (state, action) => {
      state.customerInfoForPayment = action.payload;
    },
    fetchInvoiceDetailsSummaryData: (state, { payload }) => {
      state.invoiceDetailsSummary = payload;
    },
    fetchSelectJob: (state, { payload }) => {
      state.selectJobId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchJobListAction.pending, (state) => {
      state.searchLoading = true;
    });
    builder.addCase(searchJobListAction.fulfilled, (state, { payload }) => {
      state.searchLoading = false;
      state.jobSearchList =
        payload.length > 0
          ? payload
          : [
              {
                customer: {
                  first_name: 'No search',
                  last_name: 'result found',
                  disable: true,
                },
              },
            ];
    });
    builder.addCase(searchJobListAction.rejected, (state, { payload }) => {
      state.searchLoading = false;
      state.jobSearchList = [];
      state.error = payload as ErrorState;
    });
    // =============== Branch List ===============
    builder.addCase(getBranchListAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBranchListAction.fulfilled, (state, { payload }) => {
      state.loading = false;
      const transformedData = transformBranches(payload);
      state.branchList = transformedData;
    });
    builder.addCase(getBranchListAction.rejected, (state, { payload }) => {
      state.loading = false;
      state.branchList = [];
      state.error = payload as ErrorState;
    });
    // =============== Job list with crew List ===============
    builder.addCase(getJobListWithCrewAction.pending, (state) => {
      state.jobsListLoading = true;
    });
    builder.addCase(getJobListWithCrewAction.fulfilled, (state, { payload }) => {
      state.jobsListLoading = false;
      state.jobsList = payload;
    });
    builder.addCase(getJobListWithCrewAction.rejected, (state, { payload }) => {
      state.jobsListLoading = false;
      state.jobsList = [];
      state.error = payload as ErrorState;
    });

    // =============== Customer info job data ===============
    builder.addCase(getJobDetailsAction.pending, (state) => {
      state.customerInfo.loader = true;
    });

    builder.addCase(getJobDetailsAction.fulfilled, (state, { payload }) => {
      state.customerInfo.loader = false;
      state.jobDetailsData = payload;
    });

    builder.addCase(getJobDetailsAction.rejected, (state, { payload }) => {
      state.jobDetailsData = {};
      state.error = payload as ErrorState;
      state.customerInfo.loader = false;
    });

    // =============== Customer info job data ===============
    builder.addCase(jobDetailsSaveCrewNotesAction.pending, (state) => {
      state.jobDetailsDataLoading = true;
    });

    builder.addCase(jobDetailsSaveCrewNotesAction.fulfilled, (state) => {
      state.jobDetailsDataLoading = false;
    });

    builder.addCase(jobDetailsSaveCrewNotesAction.rejected, (state, { payload }) => {
      state.error = payload as ErrorState;
      state.jobDetailsDataLoading = false;
    });
  },
});

export const jobAction = jobSlice.actions;

export default jobSlice.reducer;

const adjustTime = (dateString: DateString, customTime: string): DateString => {
  const date = new Date(dateString);
  const [hours, minutes] = customTime.split(':');
  date.setUTCHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
  return date.toISOString();
};

const getFilteredJobs = (jobs: Job[], filter: string): Job[] => {
  switch (filter.toLowerCase()) {
    case 'confirmed':
      return jobs.filter((job) => job.status?.toLowerCase() === 'confirmed');
    case 'unconfirmed':
      return jobs.filter((job) => job.status?.toLowerCase() !== 'confirmed');
    case 'junk':
      return jobs.filter((job) => job.service_type?.toLowerCase() === 'junk');
    case 'moves':
      return jobs.filter((job) => job.service_type?.toLowerCase() !== 'junk');
    case 'all':
      return jobs;
    default:
      return [];
  }
};
